import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { classService } from '../services/classService';
import '../styles/AccountSettings.css';

const ClassInvitePage = () => {
  const { invitationId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    loadInvitation();
  }, [invitationId, currentUser]);

  const loadInvitation = async () => {
    try {
      const invitations = await classService.getClassInvitations(currentUser.email);
      const foundInvitation = invitations.find(inv => inv.id === invitationId);
      
      if (foundInvitation) {
        setInvitation(foundInvitation);
      } else {
        setError('Invitation not found or has already been processed.');
      }
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError('Failed to load invitation details.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    setError('');

    try {
      await classService.acceptClassInvitation(invitationId);
      navigate('/class-setup');
    } catch (err) {
      setError(err.message || 'Failed to accept invitation');
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    setAccepting(true);
    setError('');

    try {
      await classService.declineClassInvitation(invitationId);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to decline invitation');
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="account-settings-container">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="account-settings-container">
        <div className="settings-section">
          <h2>Invitation Not Found</h2>
          <p>{error || 'This invitation may have already been processed or is no longer valid.'}</p>
          <button 
            className="primary-button"
            onClick={() => navigate('/')}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-settings-container">
      <div className="settings-header">
        <h1>Class Invitation</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="settings-section">
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '30px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h2 style={{ marginBottom: '20px' }}>You've been invited!</h2>
          
          <p style={{ fontSize: '1.1rem', marginBottom: '30px' }}>
            <strong>{invitation.inviterName}</strong> has invited you to join the class:
          </p>
          
          <div style={{
            backgroundColor: '#e3f2fd',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '30px'
          }}>
            <h3 style={{ margin: 0, color: '#1976d2' }}>{invitation.className}</h3>
          </div>

          <p style={{ marginBottom: '30px', color: '#666' }}>
            By accepting this invitation, you will become a member of this class and have access to class events and resources.
          </p>

          <div className="button-group" style={{ justifyContent: 'center' }}>
            <button 
              className="primary-button"
              onClick={handleAccept}
              disabled={accepting}
              style={{ minWidth: '120px' }}
            >
              {accepting ? 'Processing...' : 'Accept'}
            </button>
            <button 
              className="secondary-button"
              onClick={handleDecline}
              disabled={accepting}
              style={{ minWidth: '120px' }}
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassInvitePage;